import { ReportDashboardLayout } from '@/Layouts/ReportDashboardLayout'
import VerticallySplitLayout from '@/Layouts/VerticallySplitLayout'
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { GFPADetailsChart } from '@/components/GFPA/DetailsReport/GFPADetailsChart'
import { GFPADetailsTable } from '@/components/GFPA/DetailsReport/GFPADetailsTable'
import { GFPATopAndBottomFiveSecuritiesTable } from '@/components/GFPA/DetailsReport/GFPATopAndBottomFiveSecuritiesTable'
import RVHeader from '@/components/ReturnVariance/RVHeader'
import {
  rvColumnFieldForTableChartLinkingValue,
  rvDetailsDefaultGroupingFields,
  rvSecurityDetailsDefaultColumnsFieldsToVisible,
  rvSecurityDetailsFieldsToHide,
  rvSecurityDetailsTopAndBottom5FieldsToVisible,
  rvTopAndBottomFiveSecuritiesFilterKey,
} from '@/constants/rv.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IDetailsDataMap,
  IRvSecurityDetailsData,
} from '@/interfaces/rvDetails.interface'
import { getRvSecurityDetailsReport } from '@/services/axiomReport.service'
import { transformChartData } from '@/utils/chart-utils/chartUtils'
import { reportDefaultColDef } from '@/utils/grid-utils/colDefs'
import { getTableColumns } from '@/utils/grid-utils/columnUtils'
import { getTopAndBottomFiveSecurities } from '@/utils/tableGridUtils'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { RVDashboardNavigation } from '@components/ReturnVariance/RVDashboardNavigation'
import { Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const RVDetailsPage: React.FC = () => {
  const selectedReport = useAppSelector((s) => s.axiomReport.selectedReport)
  const navigate = useNavigate()
  const rvSummarySelectedSeriesName = useAppSelector(
    (s) => s.axiomReport.rvReport.selectedSummarySeriesName,
  )
  const [loading, setLoading] = useState<boolean>(true)
  const [gridReference, setGridReference] = useState<AgGridReact>()
  const [securityDetailsTableColumns, setSecurityDetailsTableColumns] =
    useState<ColDef[]>([])
  const [securityDetailsTableData, setSecurityDetailsTableData] = useState<
    IRvSecurityDetailsData[]
  >([])
  const [securityDetailsChartData, setSecurityDetailsChartData] =
    useState<IDetailsDataMap>({} as IDetailsDataMap)
  const [
    rvTopAndBottomFiveSecuritiesColumns,
    setRvTopAndBottomFiveSecuritiesColumns,
  ] = useState<ColDef[]>([])
  const [rvTopAndBottomFiveSecurities, setRvTopAndBottomFiveSecurities] =
    useState<{
      topFiveSecurities: IRvSecurityDetailsData[]
      bottomFiveSecurities: IRvSecurityDetailsData[]
    }>({ topFiveSecurities: [], bottomFiveSecurities: [] })
  const [expandedRowGroupInformation, setExpandedRowGroupInformation] =
    useState<{
      name: string
      level: number
    }>({ name: '', level: 0 })

  useEffect(() => {
    const fetchSecurityDetails = async () => {
      try {
        setLoading(true)

        const detailsListResponse = await getRvSecurityDetailsReport(
          selectedReport.reportId,
        )
        const rvDetailsColumnsFields = Object.keys(detailsListResponse[0] || {})
        const rvColumnsMetaData =
          sessionStorage.getItem(AxiomReportType.RV) &&
          JSON.parse(sessionStorage.getItem(AxiomReportType.RV) as string)
        const defaultColDef = getTableColumns(
          rvColumnsMetaData,
          rvSecurityDetailsDefaultColumnsFieldsToVisible,
          rvColumnFieldForTableChartLinkingValue,
          rvDetailsColumnsFields,
          rvSecurityDetailsFieldsToHide,
        )
        const rvTopAndBottomSecuritiesColumns = getTableColumns(
          rvColumnsMetaData,
          rvSecurityDetailsTopAndBottom5FieldsToVisible,
          [],
          rvSecurityDetailsTopAndBottom5FieldsToVisible,
          [],
          { ...reportDefaultColDef, filter: false },
        )
        const rvTopAndBottomFiveSecurities = getTopAndBottomFiveSecurities(
          detailsListResponse,
          rvTopAndBottomFiveSecuritiesFilterKey,
        ) as {
          topFiveSecurities: IRvSecurityDetailsData[]
          bottomFiveSecurities: IRvSecurityDetailsData[]
        }
        const finalColDefs = [...defaultColDef]
        setSecurityDetailsTableColumns(finalColDefs)
        const chartTransformData = transformChartData(
          detailsListResponse,
          rvDetailsDefaultGroupingFields,
          rvColumnFieldForTableChartLinkingValue[0],
          '% of Portfolio',
        )
        setSecurityDetailsTableData(detailsListResponse)
        setSecurityDetailsChartData(chartTransformData)
        setRvTopAndBottomFiveSecuritiesColumns(rvTopAndBottomSecuritiesColumns)
        setRvTopAndBottomFiveSecurities(rvTopAndBottomFiveSecurities)
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    }

    if (Object.keys(selectedReport).length) {
      fetchSecurityDetails()
    } else {
      navigate('/reports')
    }
  }, [selectedReport])

  const handleExpandRowGroup = (info: { name: string; level: number }) => {
    setExpandedRowGroupInformation((prev) => ({
      ...prev,
      name: info.name,
      level: info.level,
    }))
  }
  const handleColumnRowGroupSelectionChanged = (
    groupColumnFieldName: string,
  ) => {
    const columnGroupName = groupColumnFieldName ? [groupColumnFieldName] : []
    const chartTransformedData = transformChartData(
      securityDetailsTableData,
      columnGroupName,
      rvColumnFieldForTableChartLinkingValue[0],
      '% of Portfolio',
    )
    setSecurityDetailsChartData(chartTransformedData)
  }
  return (
    <ReportDashboardLayout
      selectedReport={selectedReport}
      navigation={<RVDashboardNavigation />}
    >
      {loading ? (
        <LoadingCard />
      ) : (
        <VerticallySplitLayout
          contentTop={
            securityDetailsChartData.seriesData &&
            securityDetailsChartData.seriesData.length &&
            securityDetailsChartData.drilldownData &&
            securityDetailsChartData.drilldownData.length ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <GFPADetailsChart
                    data={securityDetailsChartData}
                    expandedRowGroupInfo={expandedRowGroupInformation}
                    reportType={selectedReport.reportType}
                    gfpaSummarySelectedSeriesName={rvSummarySelectedSeriesName}
                  />
                </Grid>
                <Grid item xs={6}>
                  <GFPATopAndBottomFiveSecuritiesTable
                    securitiesDetailsColDef={
                      rvTopAndBottomFiveSecuritiesColumns
                    }
                    securitiesData={rvTopAndBottomFiveSecurities}
                    reportType={selectedReport.reportType}
                  />
                </Grid>
              </Grid>
            ) : (
              <NoDataToDisplayCard />
            )
          }
          contentBottom={
            securityDetailsTableData.length ? (
              <GFPADetailsTable
                reportType={selectedReport.reportType}
                rowData={securityDetailsTableData}
                setDetailsTableColumns={setSecurityDetailsTableColumns}
                setGridReference={setGridReference}
                detailsColDef={securityDetailsTableColumns}
                onExpandRowGroup={handleExpandRowGroup}
                onColumnRowGroupSelectionChanged={
                  handleColumnRowGroupSelectionChanged
                }
              />
            ) : (
              <NoDataToDisplayCard />
            )
          }
          headerBottom={
            <RVHeader
              reportData={selectedReport}
              gridReference={gridReference as AgGridReact}
            />
          }
        />
      )}
    </ReportDashboardLayout>
  )
}
